/**
 * Encerrar, reabrir e excluir uma organização, sem tela (#767).
 *
 * As três usam as rotas de `/v1/operacao/revendas/{id}`, que já existem e já
 * passam pela guarda do Super Admin. Encerrar e excluir pedem justificativa,
 * que fica na trilha da organização; excluir pede também o segundo fator, e só
 * roda sobre a organização já encerrada.
 *
 * A resposta diz o que aconteceu em cada parte, pelo nome do sistema. A tela
 * diz o nome da parte (Acesso, Atendimento, Cadastro), como no resto da gestão.
 */

import type { RevendaDesprovisionada } from '@cliqcrm/contracts';
import { oQueFalta } from '@/features/acesso/confirmar-identidade';

export type AcaoDeSituacao = 'encerrar' | 'reabrir' | 'excluir';

/** O mais longo que a rota aceita (`EncerrarRevendaSchema`). */
export const MAIOR_JUSTIFICATIVA = 500;

/** Quais ações pedem a justificativa escrita. Reabrir desfaz o encerramento, e não pede. */
export function pedeJustificativa(acao: AcaoDeSituacao): boolean {
  return acao !== 'reabrir';
}

/**
 * O que falta na justificativa, ou `null` quando está boa.
 *
 * As regras são as do segundo fator (`oQueFalta`): a de excluir vai para o
 * diálogo dele já escrita, e uma frase que a rota aceitasse e o segundo fator
 * recusasse faria a pessoa reescrever no meio do caminho.
 */
export function motivoDaJustificativa(texto: string): string | null {
  if (texto.trim().length > MAIOR_JUSTIFICATIVA) {
    return `Escreva em até ${MAIOR_JUSTIFICATIVA} caracteres.`;
  }
  return oQueFalta(texto);
}

/** O nome de cada parte na tela, no lugar do nome do sistema. */
const PARTE_DO_SISTEMA: Readonly<
  Record<RevendaDesprovisionada['sistemas'][number]['sistema'], string>
> = {
  keycloak: 'Acesso',
  chatwoot: 'Atendimento',
  crm: 'Cadastro',
  postgres: 'Conexões guardadas no CliqCRM',
};

export type ResultadoDaAcao = {
  /** As partes que não terminaram, pelo nome da tela. */
  readonly partesQueFalharam: readonly string[];
  /**
   * O que fica para alguém fazer depois. Só na exclusão: no encerramento, a
   * única pendência é a conta de atendimento continuar de pé, que é o que faz
   * dele reversível, e o diálogo já disse isso antes.
   */
  readonly pendencias: readonly string[];
};

/**
 * O que a ação deixou. O cadastro legado (`crm`) falha sempre, por desenho,
 * quando a organização tinha espaço na engine de cadastro antiga: repetir não
 * resolve, e a pendência diz como apagá-lo à mão. Por isso ele não entra nas
 * partes que não terminaram. Duas falhas da mesma parte aparecem uma vez.
 */
export function resultadoDaAcao(
  acao: AcaoDeSituacao,
  resposta: RevendaDesprovisionada,
): ResultadoDaAcao {
  const partes = resposta.sistemas
    .filter((efeito) => efeito.resultado === 'falhou' && efeito.sistema !== 'crm')
    .map((efeito) => PARTE_DO_SISTEMA[efeito.sistema]);
  return {
    partesQueFalharam: [...new Set(partes)],
    pendencias: acao === 'excluir' ? resposta.pendencias : [],
  };
}

/** Terminou sem nada a mostrar: o diálogo fecha, e o aviso confirma. */
export function terminouLimpo(resultado: ResultadoDaAcao): boolean {
  return resultado.partesQueFalharam.length === 0 && resultado.pendencias.length === 0;
}

/** O aviso de quando terminou limpo. */
export const AVISO_DA_ACAO: Readonly<Record<AcaoDeSituacao, string>> = {
  encerrar: 'Organização encerrada.',
  reabrir: 'Organização reaberta.',
  excluir: 'Organização excluída.',
};
